import { useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useId } from "react";

import AccessibleDialog from "@/components/common/AccessibleDialog/AccessibleDialog";
import Trans from "@/components/Trans/Trans";

type LorebookMakePrivateModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  "data-testid"?: string;
};

const LorebookMakePrivateModal = ({
  isOpen,
  onConfirm,
  onDismiss,
  "data-testid": testId = "LorebookMakePrivateModal",
}: LorebookMakePrivateModalProps) => {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onClose={onDismiss}
      labelledBy={titleId}
      className="w-[400px] max-h-[600px] [@media(max-height:620px)]:max-h-[90vh] bg-white dark:!bg-gray-3 rounded-lg flex flex-col py-lg gap-lg"
      data-testid={testId}
    >
      <ButtonIcon
        icon={IconVariants.CLOSE}
        variant={ButtonVariant.OUTLINED}
        onClick={onDismiss}
        className="absolute top-lg right-lg"
        aria-label={t("common:close", { defaultValue: "Close" })}
        data-testid="LorebookMakePrivateModal-DismissButton"
      />
      <div className="flex flex-col gap-lg px-lg">
        <Text id={titleId} as="h2" variant={TextVariants.HEADING_6_BOLD} className="m-0 pr-10 tracking-normal">
          <Trans i18nKey="lorebook:modal.makePrivate.title" defaults="Make this Lorebook private?" />
        </Text>
        <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
          <Trans
            i18nKey="lorebook:modal.makePrivate.description"
            defaults="This removes it from every chatbot and group anyone has attached it to. Making it public again won't reattach it."
          />
        </Text>
      </div>
      <div className="flex justify-end gap-sm px-lg">
        <Button size={ButtonSize.LARGE} variant={ButtonVariant.OUTLINED} onClick={onDismiss} data-testid="LorebookMakePrivateModal-CancelButton">
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </Button>
        <Button size={ButtonSize.LARGE} onClick={onConfirm} data-testid="LorebookMakePrivateModal-ConfirmButton">
          <Trans i18nKey="lorebook:modal.makePrivate.confirm" defaults="Make Private" />
        </Button>
      </div>
    </AccessibleDialog>
  );
};

export default LorebookMakePrivateModal;
